import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
} from '@ais-ui/ui-core';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ChevronDownIcon } from 'lucide-react';
import { useReportContext } from '../ReportContext';
import { ParsedReportParams } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { useMemo } from 'react';

function shouldShowTracks(
  reportType: string | null,
  reportDetails: ParsedReportParams | null,
): boolean {
  const showTrackOption = [
    'AREA_STAT_TB',
    'MULTI_AREA',
    'PASSAGELINE_TB',
    'DATA_PASSAGELINE_MULTI',
    'PILOTBOAT_TRIPS',
    'TUGBOAT_OPERATIONS',
  ];

  if (!reportDetails || !reportDetails.params || !reportType) {
    return false;
  }

  if (
    reportType === 'AREA_STAT_TB' &&
    reportDetails.params['STG'] === 'shiplist'
  ) {
    return false;
  }
  return showTrackOption.includes(reportType);
}

function shouldShowOnlyTargetsInView(reportType: string | null): boolean {
  const showTargetsInViewOption = ['HISTORY_TRACK_TB'];

  if (!reportType) {
    return false;
  }

  return showTargetsInViewOption.includes(reportType);
}

export const shouldShowOptions = (
  reportType: string | null,
  reportDetails: ParsedReportParams | null,
): boolean => {
  return (
    shouldShowTracks(reportType, reportDetails) ||
    shouldShowOnlyTargetsInView(reportType)
  );
};

const SwitchForm = () => {
  const { messages } = useLocalization();
  const { showTracks, setShowTracks, filterByMapView, setFilterByMapView } =
    useReportContext();
  const { reportType, reportDetails } = useReportContext();
  const FormSchema = z.object({
    showTracksForm: z.boolean(),
    showTargetsInViewForm: z.boolean(),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      showTracksForm: showTracks,
      showTargetsInViewForm: filterByMapView,
    },
  });
  const showTracksInView =
    (reportType && reportType === 'HISTORY_TRACK_TB') || false;
  return (
    <Form {...form}>
      <div>
        <div className="space-y-1 p-2">
          {shouldShowTracks(reportType, reportDetails) && (
            <FormField
              control={form.control}
              name="showTracksForm"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between pb-2.5 shadow-sm">
                  <div className="space-y-0.5">
                    <FormLabel>
                      {messages.reportsTableOptionsShowTracksLabel}
                    </FormLabel>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        setShowTracks(checked);
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}
          {shouldShowOnlyTargetsInView(reportType) && (
            <FormField
              control={form.control}
              name="showTargetsInViewForm"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between">
                  <div className="space-y-0.5">
                    <FormLabel>
                      {messages.reportsTableOptionsShowOnlyTargetsLabel}
                    </FormLabel>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        setFilterByMapView(checked);
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}
        </div>
      </div>
    </Form>
  );
};

export const ReportsOptionDropDown = () => {
  const { reportType, reportDetails } = useReportContext();
  const showOptions = useMemo(
    () => shouldShowOptions(reportType, reportDetails),
    [reportType, reportDetails],
  );

  if (!showOptions) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="pr-2">
          Options <ChevronDownIcon className="size-4 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <SwitchForm />
      </PopoverContent>
    </Popover>
  );
};
